import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table, Avatar } from '@/components/ui';
import { useConstructionIssues } from './hooks/useIssues';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { IssueStatus, IssueSeverity } from './types/construction-issues.types';

const statusColors: Record<IssueStatus, string> = {
  open: 'bg-red-100 text-red-600',
  triaged: 'bg-amber-100 text-amber-600',
  assigned: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-indigo-100 text-indigo-600',
  waiting_material: 'bg-slate-100 text-slate-600',
  waiting_approval: 'bg-violet-100 text-violet-600',
  rework_created: 'bg-orange-100 text-orange-600',
  resolved: 'bg-emerald-100 text-emerald-600',
  verified: 'bg-emerald-600 text-white',
  closed: 'bg-slate-900 text-white',
  rejected: 'bg-slate-200 text-slate-500',
  cancelled: 'bg-slate-200 text-slate-400',
};

const statusLabels: Record<IssueStatus, string> = {
  open: 'Mới mở',
  triaged: 'Đã phân loại',
  assigned: 'Đã gán',
  in_progress: 'Đang xử lý',
  waiting_material: 'Chờ vật tư',
  waiting_approval: 'Chờ phê duyệt',
  rework_created: 'Đang sửa lỗi (Rework)',
  resolved: 'Đã xử lý',
  verified: 'Đã xác minh',
  closed: 'Đã đóng',
  rejected: 'Từ chối',
  cancelled: 'Đã hủy',
};

const severityColors: Record<IssueSeverity, string> = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-blue-100 text-blue-600',
  high: 'bg-orange-100 text-orange-600',
  critical: 'bg-red-600 text-white animate-pulse',
};

export default function IssueListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    issues,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useConstructionIssues(companyId);

  return (
    <PageLayout
      title="Sự cố & Vấn đề thi công (Issues)"
      description="Trung tâm xử lý các phát sinh tại hiện trường, lỗi kỹ thuật và rủi ro ảnh hưởng đến tiến độ/chi phí dự án."
      breadcrumbs={[{ label: 'Thi công' }, { label: 'Sự cố' }]}
      isLoading={isLoading}
      headerActions={
        <AuthButton
          module="quality"
          action="create"
          variant="primary"
          icon="ri-error-warning-line"
          onClick={() => navigate('./create')}
        >
          Báo cáo sự cố mới
        </AuthButton>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Sự cố đang mở"
            value={stats?.openCount || 0}
            icon="ri-error-warning-line"
            variant="danger"
          />
          <KPICard
            title="Nghiêm trọng (Critical)"
            value={stats?.criticalCount || 0}
            icon="ri-alarm-warning-line"
            variant="warning"
          />
          <KPICard
            title="Vi phạm SLA"
            value={stats?.slaBreachCount || 0}
            icon="ri-timer-flash-line"
            variant="info"
          />
          <KPICard
            title="Tổng số sự cố"
            value={stats?.totalIssues || 0}
            icon="ri-history-line"
            variant="primary"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã sự cố, tiêu đề..."
                value={filters.search}
                onSearch={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'severity', label: 'Mức độ', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả mức độ' },
                      { value: 'low', label: 'Thấp' },
                      { value: 'medium', label: 'Trung bình' },
                      { value: 'high', label: 'Cao' },
                      { value: 'critical', label: 'Nghiêm trọng' }
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã sự cố' },
              { key: 'title', title: 'Nội dung & Phân loại' },
              { key: 'assignee', title: 'Người xử lý' },
              { key: 'severity', title: 'Mức độ', align: 'center' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={issues}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary  tracking-wider">{item.issue_code}</span>;
                case 'title':
                  return (
                    <div className="flex flex-col max-w-xs">
                      <span className="font-bold text-slate-900 line-clamp-1">{item.title}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold ">{item.issue_type}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-medium italic">{item.zone?.name || 'Toàn dự án'}</span>
                      </div>
                    </div>
                  );
                case 'assignee':
                  return (
                    <div className="flex items-center gap-2">
                      <Avatar name={item.assignee?.full_name} size="xs" src={item.assignee?.avatar_url} />
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-700 underline">{item.assignee?.full_name || 'Chưa gán'}</span>
                        <span className="text-[9px] text-slate-400 italic">{item.assigned_team?.name || '---'}</span>
                      </div>
                    </div>
                  );
                case 'severity':
                  return (
                    <Badge className={`${severityColors[item.severity_code]} border-transparent text-[10px] font-bold `}>
                      {item.severity_code}
                    </Badge>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
